import { useLoading } from "../context/LoadingContext";

import React, { useState, useEffect } from "react";
import { FaRegWindowClose, FaPlus } from "react-icons/fa";
import { useParams, useNavigate } from "react-router-dom";
import { useRestaurante } from "../context/RestauranteContext";
import "../css/EditarRestaurente.css";
import BackButton from "../components/BackButton";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const diasDaSemana = [
  "Segunda-feira",
  "Terça-feira",
  "Quarta-feira",
  "Quinta-feira",
  "Sexta-feira",
  "Sábado",
  "Domingo",
];

const BASE = process.env.REACT_APP_BASE_URL;

function maskNumeroRestaurante(value) {
  const d = value.replace(/\D/g, "").slice(0, 13);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)} (${d.slice(2)}`;
  if (d.length <= 5) return `${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4)}`;
  if (d.length <= 9) return `${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 5)} ${d.slice(5)}`;
  return `${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 5)} ${d.slice(5, 9)}-${d.slice(9)}`;
}

function getToken() {
  const stored = sessionStorage.getItem("user");
  if (!stored) return "";
  return JSON.parse(stored).access_token || "";
}

const EditarRestaurante = () => {
  const { id } = useParams();
  const { editRestaurante } = useRestaurante();
  const navigate = useNavigate();
  const { setIsLoading } = useLoading();

  const [formData, setFormData] = useState({
    nome: "",
    numeroRestaurante: "",
    endereco: {
      rua: "",
      numero: "",
      bairro: "",
      cidade: "",
      estado: "",
      cep: "",
    },
    operatingHours: [],
  });

  const [imagens, setImagens] = useState({ logoImage: null, fachadaImage: null });
  const [logoFile, setLogoFile] = useState(null);
  const [fachadaFile, setFachadaFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [fachadaPreview, setFachadaPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFachada, setUploadingFachada] = useState(false);

  useEffect(() => {
    const fetchRestaurante = async () => {
      try {
        const res = await fetch(`${BASE}/api/v1/public/restaurantes/${id}`, {
          headers: { Authentication: getToken() },
        });
        if (!res.ok) throw new Error("Erro ao carregar restaurante");
        const restaurante = await res.json();
        setFormData({
          id,
          nome: restaurante.nome || "",
          numeroRestaurante: maskNumeroRestaurante(restaurante.numeroRestaurante || ""),
          endereco: {
            rua: restaurante.endereco?.rua || "",
            numero: restaurante.endereco?.numero || "",
            bairro: restaurante.endereco?.bairro || "",
            cidade: restaurante.endereco?.cidade || "",
            estado: restaurante.endereco?.estado || "",
            cep: restaurante.endereco?.cep || "",
          },
          operatingHours: restaurante.operatingHours || [],
        });
        setImagens({
          logoImage: restaurante.logoImage || null,
          fachadaImage: restaurante.fachadaImage || null,
        });
      } catch (err) {
        toast.error("Erro ao carregar dados do restaurante.");
      }
    };
    fetchRestaurante();
  }, [id]);

  const handleFileChange = (e, tipo) => {
    const file = e.target.files[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    if (tipo === "LOGO") {
      setLogoFile(file);
      setLogoPreview(previewUrl);
    } else {
      setFachadaFile(file);
      setFachadaPreview(previewUrl);
    }
  };

  const handleUploadImagem = async (tipo) => {
    const file = tipo === "LOGO" ? logoFile : fachadaFile;
    if (!file) return;

    const setUploading = tipo === "LOGO" ? setUploadingLogo : setUploadingFachada;
    const toastId = toast.loading(`Enviando imagem de ${tipo === "LOGO" ? "logo" : "fachada"}...`);
    setUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("tipo", tipo);

      const res = await fetch(`${BASE}/api/v1/restaurantes/${id}/imagem`, {
        method: "POST",
        headers: { Authentication: getToken() },
        body: formDataUpload,
      });

      if (!res.ok) throw new Error("Erro ao enviar imagem");

      toast.update(toastId, {
        render: `Imagem de ${tipo === "LOGO" ? "logo" : "fachada"} atualizada!`,
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });

      if (tipo === "LOGO") {
        setImagens((prev) => ({ ...prev, logoImage: logoPreview }));
        setLogoFile(null);
      } else {
        setImagens((prev) => ({ ...prev, fachadaImage: fachadaPreview }));
        setFachadaFile(null);
      }
    } catch {
      toast.update(toastId, {
        render: "Erro ao enviar imagem",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "imagem") {
      setFormData({ ...formData, imagem: files[0] });
    } else if (name === "numeroRestaurante") {
      setFormData({ ...formData, numeroRestaurante: maskNumeroRestaurante(value) });
    } else if (name.startsWith("endereco.")) {
      const field = name.split(".")[1];
      setFormData({
        ...formData,
        endereco: { ...formData.endereco, [field]: value },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleHorarioChange = (index, field, value) => {
    const updatedHours = [...formData.operatingHours];
    updatedHours[index][field] = value;
    setFormData({ ...formData, operatingHours: updatedHours });
  };

  const addNovoHorario = () => {
    setFormData({
      ...formData,
      operatingHours: [
        ...formData.operatingHours,
        { diaSemana: "", horarioAbertura: "", horarioFechamento: "" },
      ],
    });
  };

  const removeHorario = (index) => {
    const updatedHours = formData.operatingHours.filter((_, i) => i !== index);
    setFormData({ ...formData, operatingHours: updatedHours });
  };

  const handleSave = async () => {
    const digitos = formData.numeroRestaurante.replace(/\D/g, "");
    if (digitos.length !== 13) {
      toast.error("Informe o telefone completo no formato: 55 (66) 9 8123-1294");
      return;
    }
    const toastId = toast.loading("Salvando alterações...");
    setIsLoading(true);
    try {
      const payload = { ...formData, numeroRestaurante: digitos };
      await editRestaurante(payload);
      toast.update(toastId, {
        render: "Restaurante alterado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/restaurantes", { replace: true });
      }, 2000);
    } catch (error) {
      console.error("Erro ao salvar restaurante:", error);
      toast.update(toastId, {
        render: "Erro ao salvar restaurante!",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!formData) {
    return <div className="loading">Carregando dados do restaurante...</div>;
  }

  return (
    <div className="editar-restaurante-container">
      <ToastContainer />
      <BackButton inline />
      <h2>Editar Restaurante</h2>

      <div className="form-section">
        <label>
          Nome do Restaurante:
          <input
            type="text"
            name="nome"
            value={formData.nome}
            onChange={handleInputChange}
            placeholder="Digite o nome do restaurante"
          />
        </label>
        <label>
          Telefone do Restaurante *:
          <input
            type="text"
            name="numeroRestaurante"
            value={formData.numeroRestaurante}
            onChange={handleInputChange}
            placeholder="55 (66) 9 8123-1294"
            maxLength={19}
          />
        </label>
        {formData.numeroRestaurante && formData.numeroRestaurante.replace(/\D/g, "").length < 13 && (
          <span style={{ fontSize: "12px", color: "#c0392b", marginTop: "-10px", display: "block" }}>
            Número incompleto — informe 13 dígitos com código do país
          </span>
        )}
      </div>

      <div className="form-section">
        <h3>Imagens</h3>
        <div className="imagens-grid">
          {/* Logo */}
          <div className="imagem-card">
            <span className="imagem-card-label">Logo</span>
            <div className="imagem-preview-wrap">
              {(logoPreview || imagens.logoImage) ? (
                <img
                  src={logoPreview || imagens.logoImage}
                  alt="Logo"
                  className="imagem-preview"
                />
              ) : (
                <div className="imagem-placeholder">Sem imagem</div>
              )}
            </div>
            <label className="imagem-file-label">
              {logoFile ? logoFile.name : "Escolher arquivo"}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e, "LOGO")}
              />
            </label>
            <button
              type="button"
              className="imagem-upload-btn"
              onClick={() => handleUploadImagem("LOGO")}
              disabled={!logoFile || uploadingLogo}
            >
              {uploadingLogo ? "Enviando..." : "Enviar Logo"}
            </button>
          </div>

          {/* Fachada */}
          <div className="imagem-card">
            <span className="imagem-card-label">Fachada</span>
            <div className="imagem-preview-wrap">
              {(fachadaPreview || imagens.fachadaImage) ? (
                <img
                  src={fachadaPreview || imagens.fachadaImage}
                  alt="Fachada"
                  className="imagem-preview"
                />
              ) : (
                <div className="imagem-placeholder">Sem imagem</div>
              )}
            </div>
            <label className="imagem-file-label">
              {fachadaFile ? fachadaFile.name : "Escolher arquivo"}
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e, "FACHADA")}
              />
            </label>
            <button
              type="button"
              className="imagem-upload-btn"
              onClick={() => handleUploadImagem("FACHADA")}
              disabled={!fachadaFile || uploadingFachada}
            >
              {uploadingFachada ? "Enviando..." : "Enviar Fachada"}
            </button>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Endereço</h3>
        <div className="endereco-grid">
          {["rua", "numero", "bairro", "cidade", "estado", "cep"].map(
            (field) => (
              <label key={field}>
                {field.charAt(0).toUpperCase() + field.slice(1)}:
                <input
                  type={field === "cep" ? "text" : "text"}
                  name={`endereco.${field}`}
                  value={formData.endereco[field] || ""}
                  onChange={handleInputChange}
                  placeholder={`Digite ${field}`}
                />
              </label>
            )
          )}
        </div>
      </div>

      <div className="form-section">
        <div className="horarios-header">
          <h3>Horários de Funcionamento</h3>
          <button
            type="button"
            className="add-horario-btn"
            onClick={addNovoHorario}
          >
            <FaPlus /> Adicionar Horário
          </button>
        </div>

        {formData.operatingHours.length === 0 ? (
          <div className="no-horarios">
            <p>Nenhum horário cadastrado</p>
          </div>
        ) : (
          <div className="horarios-list">
            {formData.operatingHours.map((horario, index) => (
              <div className="horario-card" key={index}>
                <div className="horario-card-header">
                  <span>Horário #{index + 1}</span>
                  <button
                    type="button"
                    className="remove-horario-btn"
                    onClick={() => removeHorario(index)}
                  >
                    <FaRegWindowClose />
                  </button>
                </div>

                <div className="horario-fields">
                  <label>
                    Dia da Semana:
                    <select
                      value={horario.diaSemana}
                      onChange={(e) =>
                        handleHorarioChange(index, "diaSemana", e.target.value)
                      }
                    >
                      <option value="">Selecione um dia</option>
                      {diasDaSemana.map((dia) => (
                        <option key={dia} value={dia}>
                          {dia}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="time-fields">
                    <label>
                      Abertura:
                      <input
                        type="time"
                        value={horario.horarioAbertura}
                        onChange={(e) =>
                          handleHorarioChange(
                            index,
                            "horarioAbertura",
                            e.target.value
                          )
                        }
                      />
                    </label>

                    <label>
                      Fechamento:
                      <input
                        type="time"
                        value={horario.horarioFechamento}
                        onChange={(e) =>
                          handleHorarioChange(
                            index,
                            "horarioFechamento",
                            e.target.value
                          )
                        }
                      />
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-actions">
        <button
          type="button"
          className="cancel-btn"
          onClick={() => navigate("/restaurantes")}
        >
          Cancelar
        </button>
        <button type="button" className="save-btn" onClick={handleSave}>
          Salvar Alterações
        </button>
      </div>
    </div>
  );
};

export default EditarRestaurante;